import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import { handleCustomResponse } from "../utils/response";
import { staffApi, workflowApi } from "../Apis/Api";
import CardContainer from "../components/ui/CardContainer";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import ErrorAlert from "../components/ui/ErrorAlert";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/* -------------------------- Validation -------------------------- */
const validationSchema = Yup.object().shape({
  month: Yup.date().required("Month is required").nullable(),
  staff: Yup.object()
    .shape({ value: Yup.string().nullable(), label: Yup.string() })
    .nullable(),
  branch: Yup.object()
    .shape({ value: Yup.string().nullable(), label: Yup.string() })
    .nullable(),
  location: Yup.object()
    .shape({ value: Yup.string().nullable(), label: Yup.string() })
     .nullable(),
});

/* -------------------------- Main Component ---------------------- */
const StaffAttendancePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 30, total: 0, totalPages: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);

  const observerRef = useRef(null);
  const reportRef = useRef(null); // For PDF

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: { month: null, staff: null, branch: null, location: null },
  });

  const selectedMonth = watch("month");
  const selectedStaff = watch("staff");
  const selectedBranch = watch("branch");
  const selectedLocation = watch("location");

  const formatMonthValue = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const formatMonthLabel = (date) => {
    if (!date) return "No Month Selected";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  /* ----------------------- Fetch Staff ----------------------- */
  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        setError(null);
        const response = await staffApi.getStaffByClient({ want_pagination: true });
        if (response.success) {
          setStaffList(
            response.data.map((s) => ({ value: s.staff_id, label: s.staff_name || "Unknown Staff" }))
          );
        }
      } catch {
        setError(true);
      }
    };
    fetchStaffList();
  }, []);

  /* ----------------------- Fetch Branches ----------------------- */
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setError(null);
        const response = await staffApi.getClientBranches({ want_pagination: 0 });
        if (response.success) {
          setBranchList(
            response.data.map((b) => ({ value: b.branch_id.toString(), label: b.branch_name }))
          );
        }
      } catch {
        setError(true);
      }
    };
    fetchBranches();
  }, []);

  /* ----------------------- Fetch Locations ----------------------- */
  useEffect(() => {
    const fetchLocations = async () => {
      if (!selectedBranch?.value) {
        setLocationList([]);
        return;
      }
      try {
        setError(null);
        const response = await workflowApi.getBranchLocations(selectedBranch.value);
        if (response.success) {
          setLocationList(
            response.data.map((l) => ({ value: l.location_id.toString(), label: l.location_name }))
          );
        } else {
          setLocationList([]);
        }
      } catch {
        setLocationList([]);
        setError(true);
      }
    };
    fetchLocations();
  }, [selectedBranch]);

  /* --------------------- Infinite Scroll ---------------------- */
  const lastRecordRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) loadMoreData();
      });
      if (node) observerRef.current.observe(node);
    },
    [isLoadingMore, hasMore]
  );

  /* ----------------------- Fetch Data ----------------------- */
  const fetchAttendanceData = async (
    monthValue,
    staffId = null,
    branchId = null,
    locationId = null,
    page = 1,
    reset = true
  ) => {
    try {
      page === 1 ? setIsLoading(true) : setIsLoadingMore(true);
      const params = { month: monthValue, page, limit: pagination.limit };
      if (staffId) params.staff_id = staffId;
      if (branchId) params.branch_id = branchId;
      if (locationId) params.location_id = locationId;

      const response = await staffApi.getStaffAttendanceReport(params);

      if (response.success) {
        const newData = response.data || [];
        const newPag = response.pagination || {};

        if (reset || page === 1) setAttendanceData(newData);
        else setAttendanceData((prev) => [...prev, ...newData]);

        setPagination(newPag);
        setHasMore(newPag.page < newPag.totalPages);
        setShowReport(true);

        if (page === 1)
          handleCustomResponse("success", `Attendance loaded! ${newData.length} record(s).`);
      } else throw new Error(response.message);
    } catch (err) {
      handleCustomResponse("error", err.message || "Failed to load data!");
      if (reset) {
        setAttendanceData([]);
        setShowReport(false);
      }
    } finally {
      page === 1 ? setIsLoading(false) : setIsLoadingMore(false);
    }
  };

  const loadMoreData = useCallback(() => {
    if (selectedMonth && hasMore && !isLoadingMore) {
      fetchAttendanceData(
        formatMonthValue(selectedMonth),
        selectedStaff?.value,
        selectedBranch?.value,
        selectedLocation?.value,
        pagination.page + 1,
        false
      );
    }
  }, [selectedMonth, selectedStaff, selectedBranch, selectedLocation, hasMore, isLoadingMore, pagination.page]);

  const onSubmit = (data) => {
    fetchAttendanceData(
      formatMonthValue(data.month),
      data.staff?.value,
      data.branch?.value,
      data.location?.value,
      1,
      true
    );
  };

  /* ----------------------- Download Modal ----------------------- */
  const openDownloadModal = () => {
    if (!selectedMonth) {
      handleCustomResponse("error", "Select a month first!");
      return;
    }
    setShowDownloadModal(true);
  };

  const closeDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadFormat(null);
  };

  const startDownload = (format) => {
    setDownloadFormat(format);
    setShowDownloadModal(false);
    setIsGenerating(true);
  };

  /* -------------------------- HTML -------------------------- */
  const generateHTML = () => {
    const monthLabel = formatMonthLabel(selectedMonth);
    const fileName = `staff_attendance_${formatMonthValue(selectedMonth)}${
      selectedStaff ? "_" + selectedStaff.value : ""
    }${selectedBranch ? "_" + selectedBranch.value : ""}${
      selectedLocation ? "_" + selectedLocation.value : ""
    }.html`;

    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Staff Attendance - ${monthLabel}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', sans-serif; background: #f8f9fa; color: #333; padding: 20px; }
    .container { max-width: 1200px; margin: auto; background: white; padding: 30px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
    .header { text-align: center; margin-bottom: 30px; }
    .header h1 { font-size: 28px; color: #1e293b; }
    .header p { font-size: 16px; color: #64748b; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px; }
    th { background: #f1f5f9; color: #1e293b; padding: 12px 8px; text-align: left; border: 1px solid #e2e8f0; }
    td { padding: 10px 8px; border: 1px solid #e2e8f0; }
    .text-center { text-align: center; }
    .badge { padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 500; }
    .bg-success-100 { background: #d4edda; color: #155724; }
    .bg-danger-100 { background: #f8d7da; color: #721c24; }
    .bg-warning-100 { background: #fff3cd; color: #856404; }
    .bg-secondary-100 { background: #e2e8f0; color: #495057; }
    .footer { text-align: center; margin-top: 30px; color: #94a3b8; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Staff Attendance Report</h1>
      <p>${monthLabel}${selectedStaff ? " - " + selectedStaff.label : ""}${
      selectedBranch ? " - " + selectedBranch.label : ""
    }${selectedLocation ? " - " + selectedLocation.label : ""}</p>
    </div>
    <table>
      <thead>
        <tr>
          <th>S.No</th>
          <th>Staff Name</th>
          <th>Date</th>
          <th>Branch</th>
          <th>Location</th>
          <th>Check In</th>
          <th>Check Out</th>
          <th>Shift Hours</th>
          <th class="text-center">Status</th>
        </tr>
      </thead>
      <tbody>
        ${attendanceData
          .map(
            (r, i) => `
        <tr>
          <td>${(pagination.page - 1) * pagination.limit + i + 1}</td>
          <td>${r.staff_name || "-"}</td>
          <td>${r.attendance_date ? new Date(r.attendance_date).toLocaleDateString("en-GB") : "-"}</td>
          <td>${r.branch_name || "-"}</td>
          <td>${r.location_name || "-"}</td>
          <td>${r.checkin_time || "-"}</td>
          <td>${r.checkout_time || "-"}</td>
          <td>${r.shift_hours || 0}h</td>
          <td class="text-center">
            <span class="badge ${
              r.status === "Present"
                ? "bg-success-100"
                : r.status === "Absent"
                ? "bg-danger-100"
                : r.status === "Late"
                ? "bg-warning-100"
                : "bg-secondary-100"
            }">${r.status || "Unknown"}</span>
          </td>
        </tr>`
          )
          .join("")}
      </tbody>
    </table>
    <div class="footer">Generated on: ${new Date().toLocaleString()}</div>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    handleCustomResponse("success", "HTML downloaded!");
  };

  /* -------------------------- Excel -------------------------- */
  const generateExcel = () => {
    const rows = [
      ["Staff Attendance Report"],
      [
        `${formatMonthLabel(selectedMonth)}${
          selectedStaff ? " - " + selectedStaff.label : ""
        }${selectedBranch ? " - " + selectedBranch.label : ""}${
          selectedLocation ? " - " + selectedLocation.label : ""
        }`,
      ],
      [],
      ["#", "Staff Name", "Date", "Branch", "Location", "Check In", "Check Out", "Shift Hours", "Status"],
    ];

    attendanceData.forEach((r, i) => {
      rows.push([
        (pagination.page - 1) * pagination.limit + i + 1,
        r.staff_name || "-",
        r.attendance_date ? new Date(r.attendance_date).toLocaleDateString("en-GB") : "-",
        r.branch_name || "-",
        r.location_name || "-",
        r.checkin_time || "-",
        r.checkout_time || "-",
        `${r.shift_hours || 0}h`,
        r.status || "Unknown",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, `staff_attendance_${formatMonthValue(selectedMonth)}.xlsx`);
    handleCustomResponse("success", "Excel downloaded!");
  };

 /* -------------------------- PDF with Zoom Fix -------------------------- */
const generatePDF = async () => {
  if (!reportRef.current) return;

  // Store current zoom and theme settings
  const currentZoom = document.documentElement.style.zoom;
  const currentTransform = document.documentElement.style.transform;
  const currentTransformOrigin = document.documentElement.style.transformOrigin;
  const currentWidth = document.documentElement.style.width;
  const currentHeight = document.documentElement.style.height;
  const currentTheme = document.documentElement.getAttribute('data-theme');

  try {
    // Temporarily reset zoom to 100%
    document.documentElement.style.zoom = "100%";
    document.documentElement.style.transform = "";
    document.documentElement.style.transformOrigin = "";
    document.documentElement.style.width = "";
    document.documentElement.style.height = "";
    
    // Force light theme for PDF generation
    if (currentTheme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'light');
    }

    // Wait for theme change and DOM re-render
    await new Promise(resolve => setTimeout(resolve, 200));

    // Generate PDF
    const canvas = await html2canvas(reportRef.current, { 
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false
    });
    
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? "landscape" : "portrait",
      unit: "px",
      format: [canvas.width, canvas.height],
    });
    
    pdf.addImage(img, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save(`staff_attendance_${formatMonthValue(selectedMonth)}.pdf`);
    
    handleCustomResponse("success", "PDF downloaded!");
  } catch (error) {
    console.error("PDF generation error:", error);
    handleCustomResponse("error", "Failed to generate PDF.");
  } finally {
    // Restore original zoom settings
    document.documentElement.style.zoom = currentZoom;
    document.documentElement.style.transform = currentTransform;
    document.documentElement.style.transformOrigin = currentTransformOrigin;
    document.documentElement.style.width = currentWidth;
    document.documentElement.style.height = currentHeight;
    
    // Restore original theme
    if (currentTheme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }
};
  /* ----------------------- Run Download ------------------- */
  useEffect(() => {
    if (!isGenerating || !downloadFormat) return;
    const run = async () => {
      try {
        if (downloadFormat === "html") generateHTML();
        else if (downloadFormat === "excel") generateExcel();
        else if (downloadFormat === "pdf") await generatePDF();
      } catch (err) {
        handleCustomResponse("error", "Failed to generate file.");
      } finally {
        setIsGenerating(false);
        setDownloadFormat(null);
      }
    };
    run();
  }, [isGenerating, downloadFormat]);

  /* -------------------------- Table -------------------------- */
  const renderTable = () => {
    if (!attendanceData.length) {
      return (
        <TableDataNoAvailable
          message={
            selectedMonth || selectedStaff || selectedBranch || selectedLocation
              ? "No attendance data matches your selection."
              : "No attendance data available."
          }
        />
      );
    }

    return (
      <div style={{ opacity: isLoading ? 0.4 : 1, pointerEvents: isLoading ? "none" : "auto" }}>
        {/* Hidden printable version */}
        <div style={{ position: "absolute", left: "-9999px" }} ref={reportRef}>
          <div style={{ padding: "30px", fontFamily: "'Segoe UI', sans-serif", background: "#fff" }}>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h1 style={{ fontSize: "28px", color: "#1e293b" }}>Staff Attendance Report</h1>
              <p style={{ fontSize: "16px", color: "#64748b" }}>
                {formatMonthLabel(selectedMonth)}
                {selectedStaff && ` - ${selectedStaff.label}`}
                {selectedBranch && ` - ${selectedBranch.label}`}
                {selectedLocation && ` - ${selectedLocation.label}`}
              </p>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>S.No</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Staff Name</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Date</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Branch</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Location</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Check In</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Check Out</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Shift Hours</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {attendanceData.map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.staff_name || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.attendance_date ? new Date(r.attendance_date).toLocaleDateString("en-GB") : "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.branch_name || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.location_name || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.checkin_time || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.checkout_time || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.shift_hours || 0}h</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        background: r.status === "Present" ? "#d4edda" : r.status === "Absent" ? "#f8d7da" : r.status === "Late" ? "#fff3cd" : "#e2e8f0",
                        color: r.status === "Present" ? "#155724" : r.status === "Absent" ? "#721c24" : r.status === "Late" ? "#856404" : "#495057"
                      }}>{r.status || "Unknown"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: "center", marginTop: "30px", color: "#94a3b8", fontSize: "12px" }}>
              Generated on: {new Date().toLocaleString()}
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table bordered-table table-sm mb-0">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Staff Name</th>
                <th>Date</th>
                <th>Branch</th>
                <th>Location</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Shift Hours</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {attendanceData.map((r, i) => (
                <tr
                  key={`${r.staff_id}-${r.attendance_date}-${i}`}
                  ref={i === attendanceData.length - 1 ? lastRecordRef : null}
                >
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td>{r.staff_name || "-"}</td>
                  <td>{r.attendance_date ? new Date(r.attendance_date).toLocaleDateString("en-GB") : "-"}</td>
                  <td>{r.branch_name || "-"}</td>
                  <td>{r.location_name || "-"}</td>
                  <td>{r.checkin_time || "-"}</td>
                  <td>{r.checkout_time || "-"}</td>
                  <td>{r.shift_hours || 0}h</td>
                  <td className="text-center">
                    <span className={`badge ${
                      r.status === "Present" ? "bg-success-100 text-success-600" :
                      r.status === "Absent" ? "bg-danger-100 text-danger-600" :
                      r.status === "Late" ? "bg-warning-100 text-warning-600" :
                      "bg-secondary-100 text-secondary-600"
                    }`}>{r.status || "Unknown"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isLoadingMore && (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading more...</span>
            </div>
            <span className="text-secondary-light ms-2">Loading more records...</span>
          </div>
        )}

        {!hasMore && attendanceData.length > 0 && (
          <div className="text-center py-3">
            <span className="text-secondary-light">No more records to load</span>
          </div>
        )}
      </div>
    );
  };

  /* -------------------------- Cleanup -------------------------- */
  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  if (error) return <ErrorAlert error={error} />;

  return (
    <>
      <Breadcrumb title="Staff Attendance Report" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)} className="mb-24">
            <div className="row g-3 g-md-4 align-items-end">
              {/* Month */}
              <div className="col-md-3">
                <label className="form-label fw-medium text-neutral-900">Select Month *</label>
                <Controller
                  name="month"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      selected={field.value}
                      onChange={field.onChange}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      placeholderText="Select Month"
                      className="form-control cursor-pointer"
                      wrapperClassName="w-100"
                      isClearable={false}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  )}
                />
                {errors.month && <div className="text-danger-600 text-sm mt-1">{errors.month.message}</div>}
              </div>

              {/* Staff, Branch, Location */}
              <div className="col-md-3">
                <label className="form-label fw-medium text-neutral-900">Select Staff</label>
                <Controller
                  name="staff"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect {...field} options={staffList} placeholder="Select Staff (Optional)" isClearable />
                  )}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-medium text-neutral-900">Branch</label>
                <Controller
                  name="branch"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect {...field} options={branchList} placeholder="Select Branch (Optional)" isClearable />
                  )}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-medium text-neutral-900">Location</label>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={locationList}
                      placeholder="Select Location (Optional)"
                      isClearable
                      isDisabled={!selectedBranch}
                      noOptionsMessage={() => selectedBranch ? "No locations" : "Select branch first"}
                    />
                  )}
                />
              </div>

              {/* Buttons */}
              <div className="col-12">
                <div className="d-flex gap-3">
                  <button
                    type="submit"
                    disabled={isLoading || !isValid}
                    className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:eye" width="18" />
                        View Report
                      </>
                    )}
                  </button>

                  {showReport && (
                    <button
                      type="button"
                      onClick={openDownloadModal}
                      disabled={isGenerating}
                      className="btn btn-success-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                    >
                      {isGenerating ? (
                        <>
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Generating...</span>
                          </div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:download" width="18" />
                          Download
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>

          {showReport && (
            <CardContainer className="card border">
              <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <h6 className="text-lg mb-0">
                  Attendance Report - {formatMonthLabel(selectedMonth)}
                  {selectedStaff && ` - ${selectedStaff.label}`}
                  {selectedBranch && ` - ${selectedBranch.label}`}
                  {selectedLocation && ` - ${selectedLocation.label}`}
                </h6>
                <div className="d-flex align-items-center gap-3">
                  {pagination.total > 0 && (
                    <span className="text-sm text-neutral-600">
                      Showing {attendanceData.length} of {pagination.total} records
                    </span>
                  )}
                  {attendanceData.length > 0 && (
                    <span className="badge bg-primary-100 text-primary-600 px-12 py-6">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                  )}
                </div>
              </div>
              <div className="card-body p-0">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-neutral-500 mt-2">Loading attendance data...</p>
                  </div>
                ) : (
                  renderTable()
                )}
              </div>
            </CardContainer>
          )}
        </div>
      </div>

      {/* --------------------- DOWNLOAD MODAL --------------------- */}
      <div
        className={`modal fade ${showDownloadModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{ display: showDownloadModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">
                {/* <Icon icon="mdi:file-download" className="me-2" /> */}
                Download Report
              </h5>
              <button type="button" className="btn-close" onClick={closeDownloadModal} />
            </div>
            <div className="modal-body pt-2">
              <p className="text-neutral-700 mb-3">Choose format:</p>
              <div className="d-grid gap-2">
                {/* <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
                  onClick={() => startDownload("html")}
                  disabled={isGenerating}
                >
                  <Icon icon="mdi:language-html5" width="20" />
                  HTML Report
                  {isGenerating && downloadFormat === "html" && <div className="spinner-border spinner-border-sm ms-auto" />}
                </button> */}
                <button
                  type="button"
                  className="btn btn-outline-success d-flex align-items-center justify-content-center gap-2"
                  onClick={() => startDownload("excel")}
                  disabled={isGenerating}
                >
                  <Icon icon="mdi:microsoft-excel" width="20" />
                  Excel (.xlsx)
                  {isGenerating && downloadFormat === "excel" && <div className="spinner-border spinner-border-sm ms-auto" />}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger d-flex align-items-center justify-content-center gap-2"
                  onClick={() => startDownload("pdf")}
                  disabled={isGenerating}
                >
                  <Icon icon="mdi:file-pdf-box" width="20" />
                  PDF Document
                  {isGenerating && downloadFormat === "pdf" && <div className="spinner-border spinner-border-sm ms-auto" />}
                </button>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={closeDownloadModal}
                disabled={isGenerating}
              >
                {/* <Icon icon="mdi:close" width="18" /> */}
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StaffAttendancePage;